import * as Sentry from "@sentry/react";
import { isPersistedTourState } from "./types";
import type { PersistedTourState, TourStates } from "./types";

export const STORAGE_KEY_PREFIX = "happydoc_tours_";

export function readTourStates(userId: string): TourStates {
    try {
        const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${userId}`);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
            console.warn("[Tours] Corrupt tour state in localStorage — removing");
            Sentry.captureMessage("Corrupt tour state in localStorage", {
                level: "warning",
                extra: { userId, rawSnippet: raw?.slice(0, 200) },
            });
            localStorage.removeItem(`${STORAGE_KEY_PREFIX}${userId}`);
            return {};
        }

        // Validate each entry's shape and strip invalid ones
        const result: TourStates = {};
        let hadInvalid = false;
        for (const [key, value] of Object.entries(parsed)) {
            if (isPersistedTourState(value)) {
                result[key as keyof TourStates] = value;
            } else {
                hadInvalid = true;
                Sentry.captureMessage("Invalid tour state entry in localStorage", {
                    level: "warning",
                    extra: { userId, tourId: key, value: JSON.stringify(value).slice(0, 200) },
                });
            }
        }
        // Write back cleaned data so invalid entries don't trigger Sentry on every page load.
        // Inner try/catch: a setItem failure here should not discard the valid entries we already parsed.
        if (hadInvalid) {
            try {
                localStorage.setItem(`${STORAGE_KEY_PREFIX}${userId}`, JSON.stringify(result));
            } catch (e) {
                console.warn("[Tours] Failed to clean up invalid tour state entries:", e);
            }
        }
        return result;
    } catch (e) {
        console.warn("[Tours] Failed to read tour state from localStorage:", e);
        Sentry.captureException(e, {
            extra: { context: "tour-persistence-read", userId },
        });
        // Best-effort: clear unreadable data to prevent repeated failures on every page load
        try {
            localStorage.removeItem(`${STORAGE_KEY_PREFIX}${userId}`);
        } catch (e) {
            console.warn("[Tours] Could not remove corrupt tour state from localStorage:", e);
        }
        return {};
    }
}

export function writeTourState(userId: string, tourId: string, state: PersistedTourState): void {
    try {
        const existing = readTourStates(userId);
        const updated = { ...existing, [tourId]: state };
        localStorage.setItem(`${STORAGE_KEY_PREFIX}${userId}`, JSON.stringify(updated));
    } catch (e) {
        const err = new Error(
            `Failed to persist tour state for "${tourId}": ${e instanceof Error ? e.message : String(e)}`,
        );
        (err as any).cause = e;
        throw err;
    }
}
